class RosviaHeader extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-mega-toggle]');
    this.menu = this.querySelector('[data-mega-menu]');
    this.mobileDialog = this.querySelector('[data-mobile-drawer]');
    this.mobileOpen = this.querySelector('[data-mobile-menu-open]');
    this.mobileClose = this.querySelector('[data-mobile-menu-close]');
    this.mobileOpen?.addEventListener('click', () => this.openDrawer());
    this.mobileClose?.addEventListener('click', () => this.mobileDialog.close());
    this.mobileDialog?.addEventListener('click', (event) => { if (event.target === this.mobileDialog) this.mobileDialog.close(); });
    this.mobileDialog?.addEventListener('close', () => this.closeDrawer());
    this.mobileDialog?.addEventListener('click', (event) => {
      const next = event.target.closest('[data-drawer-next]');
      const back = event.target.closest('[data-drawer-back]');
      if (next) this.showDrawerPanel(next.dataset.drawerNext);
      if (back) this.showDrawerPanel(back.dataset.drawerBack);
    });
    this.initSettingsModals();
    this.initSticky();
    if (!this.toggle || !this.menu) return;
    this.megaItem = this.toggle.closest('.site-header__nav-item');
    this.closeTimer = null;
    this.toggle.addEventListener('click', () => this.setOpen(this.toggle.getAttribute('aria-expanded') !== 'true'));
    this.toggle.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.setOpen(false); });
    this.megaItem?.addEventListener('mouseenter', () => this.setOpen(true));
    this.megaItem?.addEventListener('mouseleave', () => this.scheduleClose());
    this.menu.addEventListener('mouseenter', () => this.setOpen(true));
    this.menu.addEventListener('mouseleave', () => this.scheduleClose());
    this.megaItem?.addEventListener('focusout', () => this.scheduleClose());
    this.menu.addEventListener('focusin', () => this.setOpen(true));
    this.menu.addEventListener('focusout', () => this.scheduleClose());
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.setOpen(false);
    });
  }
  // Sticky header (base.css sticks the section wrapper). Once the page has scrolled past the header, it gets
  // is-condensed: on phones that folds the search row away (mobile-refinements.css; search stays in the menu drawer).
  // The wrapper's bottom margin takes up the folded height so the page never jumps, and --header-pinned-height feeds
  // scroll-padding-top so anchors and scrollIntoView land below the pinned bar.
  initSticky() {
    const wrapper = this.parentElement;
    if (!this.classList.contains('site-header--sticky') || !wrapper || !('IntersectionObserver' in window)) return;
    const search = this.querySelector('.site-header__top > .site-header__search');
    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    wrapper.after(sentinel);
    let past = false;
    const heightWith = (condensed) => { this.classList.toggle('is-condensed', condensed); return this.offsetHeight; };
    const apply = () => {
      const full = heightWith(false);
      const pinned = heightWith(true);
      const condense = past && !search?.contains(document.activeElement);
      this.classList.toggle('is-condensed', condense);
      wrapper.style.setProperty('--header-condensed-offset', `${condense ? full - pinned : 0}px`);
      document.documentElement.style.setProperty('--header-pinned-height', `${pinned}px`);
    };
    new IntersectionObserver(([entry]) => {
      past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      apply();
    }).observe(sentinel);
    let frame = 0;
    const schedule = () => { if (!frame) frame = requestAnimationFrame(() => { frame = 0; apply(); }); };
    addEventListener('resize', schedule);
    search?.addEventListener('focusout', schedule);
    // Set the pinned height now, before DOMContentLoaded handlers (e.g. FAQ #faq-* links) scroll to their targets.
    apply();
  }
  scheduleClose() {
    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => {
      const active = document.activeElement;
      if (!this.megaItem?.matches(':hover') && !this.menu.matches(':hover') && !this.megaItem?.contains(active) && !this.menu.contains(active)) this.setOpen(false);
    }, 80);
  }
  setOpen(open) {
    window.clearTimeout(this.closeTimer);
    if (window.matchMedia('(max-width: 767px)').matches) open = false;
    this.toggle.setAttribute('aria-expanded', String(open));
    this.menu.dataset.open = String(open);
  }
  // Language and currency modals: the header buttons (and the mobile menu) open them; a choice submits Shopify's
  // localization form, so the page reloads in the chosen language or currency.
  initSettingsModals() {
    this.querySelectorAll('[data-settings-open]').forEach((button) => {
      button.addEventListener('click', () => {
        const modal = this.querySelector(`[data-settings-modal="${button.dataset.settingsOpen}"]`);
        if (!modal || modal.open) return;
        this.settingsTrigger = button;
        modal.showModal();
        (modal.querySelector('[data-settings-search]') || modal.querySelector('[aria-current="true"]'))?.focus();
      });
    });
    this.querySelectorAll('[data-settings-modal]').forEach((modal) => {
      modal.addEventListener('click', (event) => {
        if (event.target === modal || event.target.closest('[data-settings-close]')) modal.close();
      });
      modal.addEventListener('close', () => {
        const search = modal.querySelector('[data-settings-search]');
        if (search && search.value) { search.value = ''; this.filterSettings(modal, ''); }
        this.settingsTrigger?.focus();
      });
      modal.querySelector('[data-settings-search]')?.addEventListener('input', (event) => this.filterSettings(modal, event.target.value));
      modal.querySelector('form')?.addEventListener('submit', () => modal.querySelectorAll('.settings-modal__option').forEach((option) => { option.setAttribute('aria-busy', 'true'); }));
    });
  }
  filterSettings(modal, value) {
    const query = value.trim().toLowerCase();
    let shown = 0;
    modal.querySelectorAll('[data-settings-item]').forEach((item) => {
      item.hidden = Boolean(query) && !item.dataset.settingsItem.includes(query);
      if (!item.hidden) shown += 1;
    });
    const empty = modal.querySelector('[data-settings-empty]');
    if (empty) empty.hidden = shown > 0;
  }
  openDrawer() {
    if (!this.mobileDialog || this.mobileDialog.open) return;
    this.drawerTrigger = document.activeElement;
    this.showDrawerPanel('root');
    this.mobileDialog.showModal();
    document.body.classList.add('mobile-nav-open');
    this.mobileOpen?.setAttribute('aria-expanded', 'true');
    this.mobileDialog.querySelector('[data-mobile-menu-close]')?.focus();
  }
  closeDrawer() {
    document.body.classList.remove('mobile-nav-open');
    this.mobileOpen?.setAttribute('aria-expanded', 'false');
    this.drawerTrigger?.focus();
  }
  showDrawerPanel(id) {
    this.mobileDialog?.querySelectorAll('[data-drawer-panel]').forEach((panel) => { panel.hidden = panel.dataset.drawerPanel !== id; });
    this.mobileDialog?.querySelector(`[data-drawer-panel="${id}"]`)?.focus();
  }
}
customElements.define('rosvia-header', RosviaHeader);
